import { captureException } from "@sentry/react";
import { getMsalInstanceAsync, isMfeMode } from "../../core/auth/useAuth";
import { tokenScopes } from "@coworker/apprestructured/src/assets/auth/msalConfig";
import {
  AccountInfo,
  InteractionRequiredAuthError,
  BrowserAuthError,
} from "@azure/msal-browser";
import { reportMessageToSentry } from "../useConfigureSentry";
import { jwtDecode } from "jwt-decode";

const redirectGuardKey = "msal.redirected";

const hasRedirected = () => {
  try {
    return sessionStorage.getItem(redirectGuardKey) === "1";
  } catch {
    return false;
  }
};

const setRedirected = () => {
  try {
    sessionStorage.setItem(redirectGuardKey, "1");
  } catch {
    // Storage can be unavailable due to browser privacy settings.
  }
};

const clearRedirected = () => {
  try {
    sessionStorage.removeItem(redirectGuardKey);
  } catch {
    // Storage can be unavailable due to browser privacy settings.
  }
};

const TOKEN_EXPIRY_BUFFER_SECONDS = 60;

const isExpired = (token: string) => {
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token);
    return (
      typeof exp === "number" &&
      exp <= Math.floor(Date.now() / 1000) + TOKEN_EXPIRY_BUFFER_SECONDS
    );
  } catch {
    return false;
  }
};

const redirectForToken = async (
  msalInstance: Awaited<ReturnType<typeof getMsalInstanceAsync>>
) => {
  if (isMfeMode() || hasRedirected()) return;

  setRedirected();
  try {
    await msalInstance.acquireTokenRedirect({ scopes: tokenScopes });
  } catch (error) {
    if (
      error instanceof BrowserAuthError &&
      error.errorCode === "interaction_in_progress"
    ) {
      return;
    }
    clearRedirected();
    throw error;
  }
};

const getMsalToken = async () => {
  const msalInstance = await getMsalInstanceAsync();
  const accounts = msalInstance.getAllAccounts();

  const isCacheCorruptionError = (msg: string) =>
    msg.includes("Unexpected token") ||
    msg.includes("JSON") ||
    msg.includes("cache") ||
    msg.includes("parsing") ||
    msg.includes("unable_to_parse") ||
    msg.includes("crypto_key_not_found") ||
    msg.includes("key_not_found");

  if (!accounts[0]) {
    // Guard against redirect loops in the no-account path (e.g. repeated calls
    // before the redirect completes, or a failed redirect that left the flag set).
    if (hasRedirected()) {
      return null;
    }
    try {
      await redirectForToken(msalInstance);
      return null;
    } catch (error) {
      if (
        error instanceof BrowserAuthError &&
        error.errorCode === "interaction_in_progress"
      ) {
        return null;
      } else {
        // Redirect failed — clear the flag so the next page load can retry.
        clearRedirected();
        reportMessageToSentry(
          `Error during acquireTokenRedirect.`,
          { accounts },
          "warning"
        );
        throw error;
      }
    }
  } else {
    try {
      const account = msalInstance.getActiveAccount() ?? accounts[0];
      const response = await msalInstance.acquireTokenSilent({
        scopes: tokenScopes,
        account: account as AccountInfo,
      });

      if (isExpired(response.accessToken)) {
        const refreshedResponse = await msalInstance.acquireTokenSilent({
          scopes: tokenScopes,
          account: account as AccountInfo,
          forceRefresh: true,
        });
        if (isExpired(refreshedResponse.accessToken)) return null;
        clearRedirected();
        return refreshedResponse.accessToken;
      }

      // Successful silent token — safe to clear any lingering redirect guard.
      clearRedirected();
      return response.accessToken;
    } catch (error: any) {
      const message = error?.message || "";

      if (
        error instanceof BrowserAuthError &&
        error.errorCode === "timed_out"
      ) {
        reportMessageToSentry(
          "MSAL silent token acquisition timed out",
          { accounts },
          "warning"
        );
        await redirectForToken(msalInstance);
        return null;
      }

      // Check and log to sentry if MSAL cache corruption, no action required atm.
      if (isCacheCorruptionError(message)) {
        reportMessageToSentry(
          `MSAL cache corruption detected`,
          { accounts, error: message },
          "error"
        );
        // Clear flag so the next attempt can retry auth from scratch.
        clearRedirected();
        return null;
      }

      // Only log refresh token expiry
      if (
        message.includes("AADSTS70008") ||
        message.includes("refresh token has expired") ||
        message.includes("inactivity")
      ) {
        reportMessageToSentry(
          `MSAL refresh token expired due to inactivity`,
          { accounts, error: message },
          "error"
        );
        // Clear flag — user will need to log in again manually.
        clearRedirected();
        return null;
      }

      // Only do interactive login if explicitly required
      if (error instanceof InteractionRequiredAuthError) {
        try {
          await redirectForToken(msalInstance);
          return null;
        } catch (loginError) {
          // Redirect failed — clear the flag so the next page load can retry.
          clearRedirected();
          reportMessageToSentry(
            `Catch block, error during InteractionRequiredAuthError`,
            { accounts: accounts[0], error: loginError },
            "warning"
          );
          return null;
        }
      } else {
        captureException(error, {
          tags: { area: "auth", flow: "token_acquisition" },
          extra: { accounts },
        });
        // Clear flag on unexpected errors so auth can recover on next load.
        clearRedirected();
        return null;
      }
    }
  }
};

let tokenRequest: Promise<string | null> | null = null;

export async function getTokenAsync() {
  if (!tokenRequest) {
    const request = getMsalToken();
    const trackedRequest: Promise<string | null> = request.finally(() => {
      if (tokenRequest === trackedRequest) tokenRequest = null;
    });
    tokenRequest = trackedRequest;
  }

  return await tokenRequest;
}
